import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../App';
import {
  Plus, Pencil, FunnelSimple, UploadSimple, DownloadSimple, FileArrowDown,
  Trash, UserCircle, UserMinus, ClockCounterClockwise, Package, Copy, Eye, EyeSlash,
} from '@phosphor-icons/react';
import ListSearch, { filterBySearch, ASSET_SEARCH_KEYS } from '../../../components/ListSearch';
import UserPicker from '../../../components/UserPicker';
import useLookups from '../../../hooks/useLookups';
import { SkeletonBlock, EmptyState, FilterChips } from '../../../components/module-ui';
import {
  listAssets, getAsset, getAssetStats, createAsset, updateAsset, deleteAsset,
  assignAsset, unassignAsset, importAssetsFile, downloadAssetsFile, downloadRdpFile,
} from '../api';
import { ModalShell, Field, Detail, STATUS_BADGE, fmtLabel, fmtDate, fmtDateTime } from '../ui';

const EMPTY_FORM = {
  name: '', asset_type: 'laptop', asset_tag: '', serial_number: '', specs: '',
  brand: '', model: '', location: '', condition: '', vendor: '', cost: '',
  monthly_cost: '',
  status: 'available', notes: '', purchase_date: '', warranty_expiry: '',
  rdp_host: '', rdp_port: '3389', rdp_username: '', rdp_password: '',
};

function rdpAddress(asset) {
  const host = (asset?.rdp_host || '').trim();
  if (!host) return '';
  const port = Number(asset?.rdp_port) || 3389;
  return port === 3389 ? host : `${host}:${port}`;
}

function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      className="btn-secondary"
      style={{ padding: '6px 10px', minHeight: 32, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
      aria-label={`Copy ${label}`}
    >
      <Copy size={14} /> {copied ? 'Copied' : label}
    </button>
  );
}

function RdpFormFields({ values, onChange, showPassword, onTogglePassword, passwordPlaceholder }) {
  return (
    <div style={{ gridColumn: '1 / -1', border: '1px solid #99F6E4', background: '#F0FDFA', borderRadius: 8, padding: 12, marginTop: 4 }}>
      <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Remote Desktop</p>
      <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
        Windows: Win+R, type <code>mstsc</code>, paste the IP. Mac: open Windows App, Add PC, paste the IP.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="RDP IP / hostname">
          <input type="text" value={values.rdp_host || ''} className="input-field" autoComplete="off"
            placeholder="10.0.0.12 or pc-name"
            onChange={(e) => onChange({ rdp_host: e.target.value })} />
        </Field>
        <Field label="RDP port">
          <input type="number" value={values.rdp_port ?? 3389} className="input-field"
            onChange={(e) => onChange({ rdp_port: e.target.value })} />
        </Field>
        <Field label="RDP username">
          <input type="text" value={values.rdp_username || ''} className="input-field" autoComplete="off"
            placeholder="DOMAIN\\user or .\Administrator"
            onChange={(e) => onChange({ rdp_username: e.target.value })} />
        </Field>
        <Field label="RDP password">
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={values.rdp_password || ''}
              className="input-field"
              autoComplete="new-password"
              placeholder={passwordPlaceholder || 'Leave blank to keep current'}
              onChange={(e) => onChange({ rdp_password: e.target.value })}
              style={{ flex: 1 }}
            />
            <button type="button" className="btn-secondary" aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{ minWidth: 40, minHeight: 40, padding: 8 }}
              onClick={onTogglePassword}>
              {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>
      </div>
    </div>
  );
}

function InventoryPage() {
  const { user: currentUser } = useAuth();
  const isAdmin = ['super_admin', 'admin'].includes(currentUser?.role);

  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ type: 'all', status: 'all', assignment: 'all' });
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showRdpPassword, setShowRdpPassword] = useState(false);

  // Assignment (inside view modal)
  const [assignPick, setAssignPick] = useState(null);
  const [assignNote, setAssignNote] = useState('');

  // Import
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const { lookups: typeOptions } = useLookups('asset_type');
  const { lookups: statusOptions } = useLookups('asset_status');
  const { lookups: conditionOptions } = useLookups('asset_condition');

  const fetchAssets = useCallback(async () => {
    try {
      setAssets(await listAssets());
    } catch (e) {
      console.error('Failed to fetch assets:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await getAssetStats());
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  }, []);

  useEffect(() => { fetchAssets(); fetchStats(); }, [fetchAssets, fetchStats]);

  useEffect(() => {
    let result = [...assets];
    if (filters.type !== 'all') result = result.filter((a) => a.asset_type === filters.type);
    if (filters.status !== 'all') result = result.filter((a) => a.status === filters.status);
    if (filters.assignment === 'assigned') result = result.filter((a) => a.assigned_to || a.assigned_to_name);
    if (filters.assignment === 'unassigned') result = result.filter((a) => !a.assigned_to && !a.assigned_to_name);
    result = filterBySearch(result, searchQuery, ASSET_SEARCH_KEYS);
    setFilteredAssets(result);
  }, [assets, filters, searchQuery]);

  const refresh = () => { fetchAssets(); fetchStats(); };

  // ---- create ----
  const handleCreateAsset = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...formData };
      ['asset_tag', 'serial_number', 'specs', 'brand', 'model', 'location',
        'condition', 'vendor', 'notes'].forEach((k) => { if (!payload[k]) delete payload[k]; });
      payload.cost = payload.cost ? Number(payload.cost) : undefined;
      payload.monthly_cost = payload.monthly_cost ? Number(payload.monthly_cost) : undefined;
      if (!payload.purchase_date) delete payload.purchase_date;
      if (!payload.warranty_expiry) delete payload.warranty_expiry;
      ['rdp_host', 'rdp_username'].forEach((k) => { if (!payload[k]) delete payload[k]; });
      payload.rdp_port = payload.rdp_port ? Number(payload.rdp_port) : 3389;
      if (!payload.rdp_password) delete payload.rdp_password;
      await createAsset(payload);
      setShowCreateModal(false);
      setFormData(EMPTY_FORM);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create asset');
    } finally {
      setSaving(false);
    }
  };

  // ---- update ----
  const handleUpdateAsset = async () => {
    setSaving(true);
    setError(null);
    try {
      const a = selectedAsset;
      const payload = {
        name: a.name, asset_type: a.asset_type, asset_tag: a.asset_tag,
        serial_number: a.serial_number, specs: a.specs, brand: a.brand,
        model: a.model, location: a.location, condition: a.condition,
        vendor: a.vendor, status: a.status, notes: a.notes,
        cost: a.cost === '' || a.cost == null ? null : Number(a.cost),
        monthly_cost: a.monthly_cost === '' || a.monthly_cost == null ? null : Number(a.monthly_cost),
      };
      payload.rdp_host = a.rdp_host || null;
      payload.rdp_username = a.rdp_username || null;
      payload.rdp_port = a.rdp_port === '' || a.rdp_port == null ? 3389 : Number(a.rdp_port);
      if (a.rdp_password) payload.rdp_password = a.rdp_password;
      const updated = await updateAsset(a.asset_id, payload);
      setSelectedAsset(updated);
      setEditMode(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  // ---- delete ----
  const handleDeleteAsset = async () => {
    if (!window.confirm(`Delete asset ${selectedAsset.asset_tag || selectedAsset.asset_id}? This cannot be undone.`)) return;
    try {
      await deleteAsset(selectedAsset.asset_id);
      setShowViewModal(false);
      setSelectedAsset(null);
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete asset');
    }
  };

  // ---- assign / unassign ----
  const handlePickAssignee = (user) => {
    setAssignPick(user);
  };

  const handleAssign = async () => {
    if (!assignPick) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await assignAsset(selectedAsset.asset_id, {
        assigned_to: assignPick.user_id,
        note: assignNote || undefined,
      });
      setSelectedAsset(updated);
      setAssignPick(null);
      setAssignNote('');
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign asset');
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await unassignAsset(selectedAsset.asset_id);
      setSelectedAsset(updated);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to unassign asset');
    } finally {
      setSaving(false);
    }
  };

  // ---- import / export ----
  const downloadBlob = async (path, filename) => {
    try {
      await downloadAssetsFile(path, filename);
    } catch (e) {
      alert('Download failed');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      setImportResult(await importAssetsFile(importFile));
      refresh();
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  const openView = async (asset) => {
    setSelectedAsset(asset);
    setShowViewModal(true);
    setEditMode(false);
    setError(null);
    setAssignPick(null);
    setAssignNote('');
    setShowRdpPassword(false);
    setAssignPickClients([]);
    setSelectedAssignClientId('');
    if (asset?.asset_id) {
      try {
        setSelectedAsset(await getAsset(asset.asset_id));
      } catch (e) {
        console.error('Failed to load asset details:', e);
      }
    }
  };

  const statCards = stats ? [
    { label: 'Total Assets', value: stats.total, color: '#002FA7' },
    { label: 'Assigned', value: stats.assigned, color: '#2563EB' },
    { label: 'Unassigned', value: stats.unassigned, color: '#059669' },
    { label: 'In Repair', value: (stats.by_status?.in_repair || 0) + (stats.by_status?.maintenance || 0), color: '#D97706' },
  ] : [];

  const filterChips = [];
  if (filters.type !== 'all') {
    const label = typeOptions.find((o) => o.key === filters.type)?.label || fmtLabel(filters.type);
    filterChips.push({ id: 'type', label: `Type: ${label}`, onRemove: () => setFilters({ ...filters, type: 'all' }) });
  }
  if (filters.status !== 'all') {
    const label = statusOptions.find((o) => o.key === filters.status)?.label || fmtLabel(filters.status);
    filterChips.push({ id: 'status', label: `Status: ${label}`, onRemove: () => setFilters({ ...filters, status: 'all' }) });
  }
  if (filters.assignment !== 'all') {
    const label = filters.assignment === 'assigned' ? 'Assigned' : 'Unassigned';
    filterChips.push({ id: 'assignment', label: `Assignment: ${label}`, onRemove: () => setFilters({ ...filters, assignment: 'all' }) });
  }

  return (
    <div data-testid="assets-page">

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <button onClick={() => downloadBlob('/assets/import/template', 'asset_import_template.xlsx')}
          className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }} data-testid="template-button">
          <FileArrowDown size={18} /> Template
        </button>
        <button onClick={() => downloadBlob('/assets/export', 'assets_export.xlsx')}
          className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }} data-testid="export-button">
          <DownloadSimple size={18} /> Export
        </button>
        <button onClick={() => { setShowImportModal(true); setImportFile(null); setImportResult(null); }}
          className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }} data-testid="import-button">
          <UploadSimple size={18} /> Import
        </button>
        <button onClick={() => { setFormData(EMPTY_FORM); setError(null); setShowRdpPassword(false); setShowCreateModal(true); }}
          className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="create-asset-button">
          <Plus size={18} weight="bold" /> Add Asset
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', marginBottom: '8px' }}>
          {statCards.map((c) => (
            <div key={c.label} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>{c.label}</p>
              <p style={{ fontSize: '15px', fontWeight: '700', color: c.color }}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '10px', marginBottom: '8px', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <FunnelSimple size={20} weight="bold" />
          <span style={{ fontSize: '12px', fontWeight: '600' }}>Filters</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Asset Type</label>
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="input-field" style={{ padding: '6px 8px', fontSize: '12px' }}>
              <option value="all">All Types</option>
              {typeOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="input-field" style={{ padding: '6px 8px', fontSize: '12px' }}>
              <option value="all">All Status</option>
              {statusOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Assignment</label>
            <select value={filters.assignment} onChange={(e) => setFilters({ ...filters, assignment: e.target.value })}
              className="input-field" style={{ padding: '6px 8px', fontSize: '12px' }}>
              <option value="all">All</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </div>
        </div>
      </div>

      <FilterChips
        chips={filterChips}
        onClearAll={() => setFilters({ type: 'all', status: 'all', assignment: 'all' })}
      />

      <ListSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by tag, name, serial, specs, user…"
        resultCount={filteredAssets.length}
        totalCount={assets.length}
      />

      {loading ? (
        <SkeletonBlock />
      ) : filteredAssets.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No assets found"
          description="Add one manually or import from a file."
          action={(
            <button
              type="button"
              onClick={() => { setFormData(EMPTY_FORM); setError(null); setShowRdpPassword(false); setShowCreateModal(true); }}
              className="btn-primary"
            >
              Add Asset
            </button>
          )}
        />
      ) : (
        <div>
          <div style={{ marginBottom: '6px', fontSize: '12px', color: '#6B7280' }}>
            Showing {filteredAssets.length} of {assets.length} assets
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Asset Tag</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Serial</th>
                  <th>RDP IP</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map((asset) => (
                  <tr key={asset.asset_id} data-testid="asset-row">
                    <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: '600' }}>
                      <button onClick={() => openView(asset)}
                        style={{ background: 'none', border: 'none', color: '#002FA7', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 'inherit' }}
                        data-testid="asset-id-link">
                        {asset.asset_tag || asset.asset_id}
                      </button>
                    </td>
                    <td>{asset.name}</td>
                    <td style={{ textTransform: 'capitalize' }}>{fmtLabel(asset.asset_type)}</td>
                    <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{asset.serial_number || '—'}</td>
                    <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{asset.rdp_host || '—'}</td>
                    <td>
                      <span className="badge" style={{ background: STATUS_BADGE[asset.status] || '#F3F4F6', textTransform: 'capitalize' }}>
                        {fmtLabel(asset.status)}
                      </span>
                    </td>
                    <td>{asset.assigned_to_name || 'Unassigned'}</td>
                    <td>
                      <button onClick={() => openView(asset)} className="btn-secondary"
                        style={{ padding: '4px 12px', fontSize: '12px' }} data-testid="view-asset-button">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------------- Create Modal ---------------- */}
      {showCreateModal && (
        <ModalShell title="Add New Asset" onClose={() => setShowCreateModal(false)} testid="create-asset-modal">
          <form onSubmit={handleCreateAsset}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Field label="Asset Name *">
                <input type="text" value={formData.name} required className="input-field"
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="asset-name-input" />
              </Field>
              <Field label="Asset Type *">
                <select value={formData.asset_type} className="input-field"
                  onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })} data-testid="asset-type-select">
                  {typeOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Asset Tag"><input type="text" value={formData.asset_tag} className="input-field"
                placeholder="Auto-generated if blank"
                onChange={(e) => setFormData({ ...formData, asset_tag: e.target.value })} data-testid="asset-tag-input" /></Field>
              <Field label="Serial / Service Tag"><input type="text" value={formData.serial_number} className="input-field"
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} data-testid="asset-serial-input" /></Field>
              <Field label="Brand"><input type="text" value={formData.brand} className="input-field"
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })} /></Field>
              <Field label="Model"><input type="text" value={formData.model} className="input-field"
                onChange={(e) => setFormData({ ...formData, model: e.target.value })} /></Field>
              <Field label="Status">
                <select value={formData.status} className="input-field"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })} data-testid="asset-status-select">
                  {statusOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Condition">
                <select value={formData.condition} className="input-field"
                  onChange={(e) => setFormData({ ...formData, condition: e.target.value })}>
                  <option value="">—</option>
                  {conditionOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Location"><input type="text" value={formData.location} className="input-field"
                onChange={(e) => setFormData({ ...formData, location: e.target.value })} /></Field>
              <Field label="Vendor"><input type="text" value={formData.vendor} className="input-field"
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })} /></Field>
              <Field label="Purchase cost"><input type="number" step="0.01" value={formData.cost} className="input-field"
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })} /></Field>
              <Field label="Monthly cost"><input type="number" step="0.01" value={formData.monthly_cost} className="input-field"
                onChange={(e) => setFormData({ ...formData, monthly_cost: e.target.value })} /></Field>
              <Field label="Purchase Date"><input type="date" value={formData.purchase_date} className="input-field"
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} /></Field>
              <Field label="Warranty Expiry"><input type="date" value={formData.warranty_expiry} className="input-field"
                onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })} /></Field>
              <RdpFormFields
                values={formData}
                onChange={(patch) => setFormData({ ...formData, ...patch })}
                showPassword={showRdpPassword}
                onTogglePassword={() => setShowRdpPassword((v) => !v)}
                passwordPlaceholder="Windows login password"
              />
            </div>
            <Field label="Specifications">
              <textarea value={formData.specs} className="input-field" rows={2}
                onChange={(e) => setFormData({ ...formData, specs: e.target.value })} />
            </Field>
            <Field label="Notes">
              <textarea value={formData.notes} className="input-field" rows={2}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
            </Field>
            {error && <p style={{ color: '#B91C1C', fontSize: '12px', marginBottom: '8px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={saving} data-testid="submit-asset-button">
                {saving ? 'Saving…' : 'Add Asset'}
              </button>
              <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary" style={{ flex: 1 }}>
                Cancel
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {/* ---------------- View / Edit Modal ---------------- */}
      {showViewModal && selectedAsset && (
        <ModalShell
          title={selectedAsset.asset_tag || selectedAsset.asset_id}
          onClose={() => { setShowViewModal(false); setEditMode(false); }}
          testid="view-asset-modal"
          headerActions={!editMode && (
            <>
              <button onClick={() => setEditMode(true)} className="btn-primary" style={{ padding: '6px 16px', display: 'flex', alignItems: 'center', gap: '4px' }} data-testid="edit-asset-button">
                <Pencil size={16} /> Edit
              </button>
              {isAdmin && (
                <button onClick={handleDeleteAsset} className="btn-secondary" style={{ padding: '6px 12px', color: '#B91C1C', display: 'flex', alignItems: 'center', gap: '4px' }} data-testid="delete-asset-button">
                  <Trash size={16} /> Delete
                </button>
              )}
            </>
          )}
        >
          {error && <p style={{ color: '#B91C1C', fontSize: '12px', marginBottom: '6px' }}>{error}</p>}
          {editMode ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Field label="Name"><input type="text" value={selectedAsset.name || ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, name: e.target.value })} data-testid="edit-asset-name" /></Field>
              <Field label="Type">
                <select value={selectedAsset.asset_type || ''} className="input-field"
                  onChange={(e) => setSelectedAsset({ ...selectedAsset, asset_type: e.target.value })}>
                  {typeOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Asset Tag"><input type="text" value={selectedAsset.asset_tag || ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, asset_tag: e.target.value })} /></Field>
              <Field label="Serial / Service Tag"><input type="text" value={selectedAsset.serial_number || ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, serial_number: e.target.value })} /></Field>
              <Field label="Brand"><input type="text" value={selectedAsset.brand || ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, brand: e.target.value })} /></Field>
              <Field label="Model"><input type="text" value={selectedAsset.model || ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, model: e.target.value })} /></Field>
              <Field label="Status">
                <select value={selectedAsset.status || ''} className="input-field"
                  onChange={(e) => setSelectedAsset({ ...selectedAsset, status: e.target.value })} data-testid="edit-asset-status">
                  {statusOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Condition">
                <select value={selectedAsset.condition || ''} className="input-field"
                  onChange={(e) => setSelectedAsset({ ...selectedAsset, condition: e.target.value })}>
                  <option value="">—</option>
                  {conditionOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Location"><input type="text" value={selectedAsset.location || ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, location: e.target.value })} /></Field>
              <Field label="Vendor"><input type="text" value={selectedAsset.vendor || ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, vendor: e.target.value })} /></Field>
              <Field label="Purchase cost"><input type="number" step="0.01" value={selectedAsset.cost ?? ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, cost: e.target.value })} /></Field>
              <Field label="Monthly cost"><input type="number" step="0.01" value={selectedAsset.monthly_cost ?? ''} className="input-field"
                onChange={(e) => setSelectedAsset({ ...selectedAsset, monthly_cost: e.target.value })} /></Field>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Specifications"><textarea value={selectedAsset.specs || ''} className="input-field" rows={2}
                  onChange={(e) => setSelectedAsset({ ...selectedAsset, specs: e.target.value })} /></Field>
                <Field label="Notes"><textarea value={selectedAsset.notes || ''} className="input-field" rows={2}
                  onChange={(e) => setSelectedAsset({ ...selectedAsset, notes: e.target.value })} /></Field>
              </div>
              <RdpFormFields
                values={selectedAsset}
                onChange={(patch) => setSelectedAsset({ ...selectedAsset, ...patch })}
                showPassword={showRdpPassword}
                onTogglePassword={() => setShowRdpPassword((v) => !v)}
                passwordPlaceholder={selectedAsset.has_rdp_password ? 'Leave blank to keep current password' : 'Windows login password'}
              />
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button onClick={handleUpdateAsset} className="btn-primary" style={{ flex: 1 }} disabled={saving} data-testid="save-asset-button">
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button onClick={() => setEditMode(false)} className="btn-secondary" style={{ flex: 1 }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '8px' }}>
                <Detail label="Name" value={selectedAsset.name} />
                <Detail label="Type" value={fmtLabel(selectedAsset.asset_type)} capitalize />
                <Detail label="Serial / Service Tag" value={selectedAsset.serial_number} mono />
                <Detail label="Brand" value={selectedAsset.brand} />
                <Detail label="Model" value={selectedAsset.model} />
                <Detail label="Condition" value={fmtLabel(selectedAsset.condition)} capitalize />
                <Detail label="Location" value={selectedAsset.location} />
                <Detail label="Vendor" value={selectedAsset.vendor} />
                <Detail label="Purchase cost" value={selectedAsset.cost != null ? selectedAsset.cost : null} />
                <Detail label="Monthly cost" value={selectedAsset.monthly_cost != null ? selectedAsset.monthly_cost : null} />
                <Detail label="Purchase Date" value={fmtDate(selectedAsset.purchase_date)} />
                <Detail label="Warranty Expiry" value={fmtDate(selectedAsset.warranty_expiry)} />
                <div>
                  <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Status</p>
                  <span className="badge" style={{ background: STATUS_BADGE[selectedAsset.status] || '#F3F4F6', textTransform: 'capitalize' }}>
                    {fmtLabel(selectedAsset.status)}
                  </span>
                </div>
              </div>
              {selectedAsset.specs && <Detail label="Specifications" value={selectedAsset.specs} block />}
              {selectedAsset.notes && <Detail label="Notes" value={selectedAsset.notes} block />}

              <div style={{ border: '1px solid #99F6E4', background: '#F0FDFA', borderRadius: 8, padding: 12, marginTop: 12, marginBottom: 8 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Remote Desktop</p>
                  <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
                    Windows: Win+R, type <code>mstsc</code>, paste the IP. Mac: open Windows App, Add PC, paste the IP.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <Detail label="IP / hostname" value={selectedAsset.rdp_host} mono />
                    <Detail label="Port" value={selectedAsset.rdp_port || 3389} mono />
                    <Detail label="Username" value={selectedAsset.rdp_username} mono />
                    <div>
                      <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Password</p>
                      <p style={{ fontSize: 12, fontWeight: 500, fontFamily: 'IBM Plex Mono, monospace' }}>
                        {selectedAsset.rdp_password
                          ? (showRdpPassword ? selectedAsset.rdp_password : '••••••••')
                          : (selectedAsset.has_rdp_password ? '••••••••' : '—')}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <CopyBtn text={rdpAddress(selectedAsset)} label="Copy IP" />
                    <CopyBtn text={selectedAsset.rdp_username} label="Copy username" />
                    {selectedAsset.rdp_password && (
                      <>
                        <CopyBtn text={selectedAsset.rdp_password} label="Copy password" />
                        <button type="button" className="btn-secondary"
                          style={{ padding: '6px 10px', minHeight: 32, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => setShowRdpPassword((v) => !v)}>
                          {showRdpPassword ? <EyeSlash size={14} /> : <Eye size={16} />}
                          {showRdpPassword ? 'Hide password' : 'Show password'}
                        </button>
                      </>
                    )}
                    {selectedAsset.rdp_host && (
                      <button type="button" className="btn-primary"
                        style={{ padding: '6px 12px', minHeight: 32, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        onClick={() => downloadRdpFile(
                          selectedAsset.asset_id,
                          `${selectedAsset.asset_tag || selectedAsset.asset_id}.rdp`,
                        )}
                      >
                        <DownloadSimple size={14} /> Open .rdp
                      </button>
                    )}
                  </div>
                </div>

              {/* Assignment */}
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <UserCircle size={18} weight="bold" />
                  <span style={{ fontSize: '12px', fontWeight: '600' }}>Assignment</span>
                </div>
                {selectedAsset.assigned_to_name ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#F0F9FF', borderRadius: '8px', marginBottom: '6px' }}>
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: '600' }}>
                        {selectedAsset.assigned_to_name}
                        {selectedAsset.assigned_client_name && (
                          <span style={{ fontWeight: '400', color: '#6B7280' }}> · {selectedAsset.assigned_client_name}</span>
                        )}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6B7280' }}>Since {fmtDate(selectedAsset.assigned_at)}</p>
                    </div>
                    <button onClick={handleUnassign} className="btn-secondary" disabled={saving}
                      style={{ padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px' }} data-testid="unassign-button">
                      <UserMinus size={16} /> Unassign
                    </button>
                  </div>
                ) : (
                  <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>Currently unassigned.</p>
                )}
                <p style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
                  {selectedAsset.assigned_to_name ? 'Reassign to' : 'Assign to'}
                </p>
                <UserPicker value={assignPick?.user_id} onSelect={handlePickAssignee} staffOnly={false} placeholder="Search employee…" />
                <input type="text" value={assignNote} placeholder="Note (optional)" className="input-field"
                  style={{ marginTop: '8px' }} onChange={(e) => setAssignNote(e.target.value)} />
                <button onClick={handleAssign} className="btn-primary" disabled={!assignPick || saving}
                  style={{ marginTop: '8px', padding: '5px 10px', opacity: !assignPick || saving ? 0.6 : 1 }} data-testid="assign-button">
                  {saving ? 'Saving…' : 'Confirm Assignment'}
                </button>
              </div>

              {/* History */}
              {selectedAsset.history?.length > 0 && (
                <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <ClockCounterClockwise size={18} weight="bold" />
                    <span style={{ fontSize: '12px', fontWeight: '600' }}>History</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                    {[...selectedAsset.history].reverse().map((h, i) => (
                      <div key={i} style={{ fontSize: '12px', paddingLeft: '12px', borderLeft: '2px solid #E5E7EB' }}>
                        <p style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                          {fmtLabel(h.action)}
                          {h.to_user_name && ` → ${h.to_user_name}`}
                          {h.to_client_name && ` (${h.to_client_name})`}
                          {h.action === 'unassigned' && h.from_user_name && ` (was ${h.from_user_name})`}
                        </p>
                        <p style={{ color: '#6B7280', fontSize: '12px' }}>
                          {fmtDateTime(h.at)}{h.by_user_name && ` · by ${h.by_user_name}`}{h.note && ` · ${h.note}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalShell>
      )}

      {/* ---------------- Import Modal ---------------- */}
      {showImportModal && (
        <ModalShell title="Import Assets" onClose={() => setShowImportModal(false)} testid="import-asset-modal">
          <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>
            Upload an Excel (.xlsx) or CSV file. Recognised columns: <strong>ASSET TAG, ASSET TYPE, NAME, USER, SERVICE TAG, SPECS, STATUS</strong>.
            Rows are matched by Asset Tag (then Serial) — existing assets are updated, new ones created.
          </p>
          <button onClick={() => downloadBlob('/assets/import/template', 'asset_import_template.xlsx')}
            className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <FileArrowDown size={16} /> Download Template
          </button>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setImportFile(e.target.files[0])}
            style={{ display: 'block', marginBottom: '8px', width: '100%' }} data-testid="import-file-input" />

          {importResult && (
            <div style={{ padding: '12px', borderRadius: '8px', marginBottom: '8px',
              background: importResult.error ? '#FEE2E2' : '#ECFDF5', fontSize: '12px' }}>
              {importResult.error ? (
                <p style={{ color: '#991B1B' }}>{importResult.error}</p>
              ) : (
                <>
                  <p style={{ fontWeight: '600', color: '#065F46', marginBottom: '4px' }}>
                    Import complete — {importResult.created} created, {importResult.updated} updated, {importResult.skipped} skipped (of {importResult.total_rows} rows).
                  </p>
                  {importResult.errors?.length > 0 && (
                    <ul style={{ margin: '8px 0 0', paddingLeft: '18px', color: '#B91C1C' }}>
                      {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleImport} className="btn-primary" style={{ flex: 1, opacity: !importFile || importing ? 0.6 : 1 }}
              disabled={!importFile || importing} data-testid="submit-import-button">
              {importing ? 'Importing…' : 'Import'}
            </button>
            <button onClick={() => setShowImportModal(false)} className="btn-secondary" style={{ flex: 1 }}>Close</button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

export default InventoryPage;
